import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, HostListener, inject, Input, OnInit } from '@angular/core';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ButtonComponent,
	DatepickerComponent,
	FormGroupComponent,
	IconComponent,
	INavHelp,
	InputComponent,
	LinkComponent,
	NavHelpComponent,
	ProgressComponent,
	PwcsLayoutComponent,
	TileComponent,
	CodeInputComponent,
} from '@tecalisdev/infinity';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { ReactiveFormsService } from 'src/app/core/helpers/reactive-forms.service';
import { IModalClose } from 'src/app/core/models/modal-confirmation.models';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AuthService } from 'src/app/core/services/auth.service';
import { DialogConfirmationService } from 'src/app/core/services/dialog-confirmation.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { formatDateFilter } from 'src/app/core/utils/utils';
import { ModalIrpfHelpComponent } from 'src/app/shared/components/modal-irpf-help/modal-irpf-help.component';
import { ModalNifExpidationHelpComponent } from 'src/app/shared/components/modal-nif-expidation-help/modal-nif-expidation-help.component';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import {
	CONNECTION_TYPE,
	IConnectionOperation,
	IFinishOperation,
	IOperation,
	MODE,
	OPERATION_STATUS,
	OPERATION_TYPE,
} from 'src/app/user-identification/models/user-identification';
import { ConnectionsService } from 'src/app/user-identification/services/connections/connections.service';
import { ModalNieSoporteHelpComponent } from 'src/app/shared/components/modal-nie-soporte-help/modal-nie-soporte-help.component';
import { HttpErrorResponse } from '@angular/common/http';
import { OperationService } from 'src/app/shared/services/step.service';
import { ERROR_CODE } from 'src/app/shared/models/error.models';
import { MasterService } from 'src/app/shared/services/master.service';
import { ProcessService } from 'src/app/user-identification/services/process.service';
import { IMaster } from 'src/app/shared/models/master.models';
import { ConnectionsHelperService } from 'src/app/user-identification/services/connections/connections-helper.service';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';

@Component({
	selector: 'app-irpf',
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		NgClass,
		NgTemplateOutlet,
		ButtonComponent,
		FormGroupComponent,
		IconComponent,
		InputComponent,
		LinkComponent,
		NavHelpComponent,
		ProgressComponent,
		PwcsLayoutComponent,
		TileComponent,
		DatepickerComponent,
		ModalScanQrComponent,
		ModalIrpfHelpComponent,
		ModalNifExpidationHelpComponent,
		ModalNieSoporteHelpComponent,
		CodeInputComponent,
		PwcsInlineSvgComponent,
	],
	templateUrl: './irpf.component.html',
	styleUrls: ['./irpf.component.scss'],
})
export class IrpfComponent implements OnInit {
	operationService = inject(OperationService);

	@Input()
	get operations(): IOperation[] {
		return this._operations;
	}
	set operations(operations: IOperation[]) {
		this._operations = operations;
		const chooseOperation = operations.find((op) => op.type === OPERATION_TYPE.CHOOSE);
		const irpfOperation: any = operations.find(
			(op) => op.type === OPERATION_TYPE.CONNECTIONS && op.config?.type === CONNECTION_TYPE.IRPF,
		);
		/*
			- Si tengo la operacion choose no muestro el tutorial y realizo la
			comprobaciones por si tengo ya los datos necesarios e iniciar la operación
			- Si no tengo la operacion choose y tengo el campo que indica que se
			debe evaluar financieramiente el documento muestro el tutorial
		*/
		if (chooseOperation) {
			this.tutorial = false;
			this.initOperationIfHasOcrCompleted();
		} else {
			if (irpfOperation?.config?.evaluation) {
				this.tutorial = true;
			} else {
				this.tutorial = false;
				this.initOperationIfHasOcrCompleted();
			}
		}
	}
	@Input() operation: IConnectionOperation;

	public OPERATION_TYPE: any = OPERATION_TYPE;
	public MODE = MODE;
	private _operations: IOperation[];
	public mode_otp: boolean = false;
	public hasOcrOperation: boolean = false;
	public codeOtp: string = '';
	public codeOtpError: string = '';
	public errorType: 'robot' | 'server' | 'insufficient-data' | 'otp-expired' = null;
	private intervalId: any;
	public navsHelp: Array<INavHelp> = this.navHelpService.connectionsNavsHelp;
	public navsHelpTutorial: Array<INavHelp> = this.navHelpService.getConnectionsTutorialNavsHelp();
	public isVisibleScan: boolean = false;
	public isVisibleHelp: boolean = false;
	public isVisibleExpeditionHelp: boolean = false;
	public isVisibleSoporteHelp: boolean = false;
	public form: FormGroup = new FormGroup({
		nif: new FormControl('', [Validators.required, this.nifValidator.bind(this)]),
		valid_to: new FormControl('', Validators.required),
		soporte: new FormControl('', Validators.required),
		uuid_connection: new FormControl(''),
	});
	public today: Date = new Date();
	private initOperation: boolean = false;
	public tutorial: boolean = false;
	private languagePickerUi = inject(LanguagePickerUiService);

	// Contador
	public countdown: string = '5:00';
	private countdownSeconds: number = 300;
	private countdownInterval: any;

	@HostListener('document:keydown.enter')
	onEnterKeyPress() {
		if (this.mode_otp) this.sendOtpCode();
	}

	constructor(
		public designService: DesignService,
		public progressService: ProgressService,
		public reactiveFormService: ReactiveFormsService,
		public auth: AuthService,
		private trans: TranslateService,
		private connectionsService: ConnectionsService,
		private loadingService: LoadingService,
		private navHelpService: NavHelpService,
		private dialogConfirmationService: DialogConfirmationService,
		private masterService: MasterService,
		private processService: ProcessService,
		private connectionsHelper: ConnectionsHelperService,
	) {
		this.form.valueChanges.subscribe((value: { nif: string; valid_to: string; soporte: string }) => {
			if (value.nif) {
				if (this.isValidDni(value.nif)) {
					this.form.get('valid_to').setValidators(Validators.required);
					this.form.get('valid_to').updateValueAndValidity({ emitEvent: false });
					this.form.get('soporte').setValidators(null);
					this.form.get('soporte').updateValueAndValidity({ emitEvent: false });
				}
				if (this.isValidNie(value.nif)) {
					this.form.get('valid_to').setValidators(null);
					this.form.get('valid_to').updateValueAndValidity({ emitEvent: false });
					this.form.get('soporte').setValidators(Validators.required);
					this.form.get('soporte').updateValueAndValidity({ emitEvent: false });
				}
			}
		});
	}

	ngOnInit(): void {
		this.masterService.getMaster().subscribe((master: IMaster) => {
			if (master && !this.initOperation) {
				this.initOperation = true;
				this.processService.initOperation(this.operation.type);
			}
		});
		this.connectionsHelper.initNumberOfTries(this.operation);
	}

	// Mode enviar formulario
	public sendForm(): void {
		this.mode_otp = false;
		this.errorType = null;
		clearInterval(this.countdownInterval);
		if (this.hasOcrOperation || this.form.valid) {
			this.connectionsHelper.initNumberOfTries(this.operation);
			if (this.operation?.config) this.operation.config.numberOfTries++;
			this.showMultipleLoading();
			this.connectionsService
				.irpfInit({
					nif: this.form.get('nif').value || undefined,
					valid_to: this.isValidDni(this.form.get('nif').value)
						? formatDateFilter(this.form.get('valid_to').value)
						: undefined,
					soporte: this.isValidNie(this.form.get('nif').value) ? this.form.get('soporte').value : undefined,
					uuid_config: this.operation.uuid,
				})
				.subscribe({
					next: (response: any) => {
						this.mode_otp = true;
						this.startCountdown();
						this.form.get('uuid_connection').patchValue(response.uuid_connection);
					},
					error: (error: HttpErrorResponse) => {
						this.handleError(error);
					},
					complete: () => {
						this.loadingService.hide();
						clearInterval(this.intervalId);
					},
				});
		} else {
			this.form.markAllAsTouched();
		}
	}

	private initOperationIfHasOcrCompleted(): void {
		// if (this.operations.find((op) => op.type === OPERATION_TYPE.OCR)?.completed) {
		this.hasOcrOperation = true;
		this.sendForm();
		//}
	}

	public finishTutorial(): void {
		this.tutorial = false;
		this.initOperationIfHasOcrCompleted();
	}

	private showMultipleLoading(): void {
		let minute = 0;
		const loadingMessages = [
			this.trans.instant('CONNECTIONS.IRPF.LOADING.PHASE_1'),
			this.trans.instant('CONNECTIONS.IRPF.LOADING.PHASE_2'),
			this.trans.instant('CONNECTIONS.IRPF.LOADING.PHASE_3'),
		];

		this.loadingService.show(loadingMessages[0]);
		this.intervalId = setInterval(() => {
			minute++;
			const msg = loadingMessages[minute] || loadingMessages[loadingMessages.length - 1];
			this.loadingService.show(msg);
		}, 60000); // Cada minuto
	}

	// Mode enviar OTP
	public sendOtpCode(): void {
		this.codeOtpError = '';
		this.errorType = null;
		if (this.codeOtp) {
			this.loadingService.show(this.trans.instant('CONNECTIONS.IRPF.LOADING.GET_IRPF'));
			this.connectionsService
				.irpfOtp({
					uuid_config: this.operation.uuid,
					otp_code: this.codeOtp,
					uuid_connection: this.form.get('uuid_connection').value,
				})
				.subscribe({
					next: (response: {
						result: OPERATION_STATUS;
						process_uuid: string;
						process: IOperation[];
						data: any;
					}) => {
						if (response.result === OPERATION_STATUS.KO) {
							this.operations.forEach((op: IOperation) => {
								if (op.type !== OPERATION_TYPE.FINISH) {
									op.completed = true;
								}
							});
						}
						const found = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
						if (found) {
							(found as IFinishOperation).config.status = response.result;
						}
						this.operation.completed = true;
					},
					error: (error: HttpErrorResponse) => {
						this.handleError(error);
					},
					complete: () => {
						this.loadingService.hide();
					},
				});
		} else {
			this.codeOtpError = this.trans.instant('GENERAL.VALIDATION.INCORRECT_FORMAT');
		}
	}

	private handleError(error: HttpErrorResponse): void {
		this.loadingService.hide();
		clearInterval(this.intervalId);

		const retry = error?.error?.retry ?? false;
		const isDefinitiveKo = error?.error?.result === OPERATION_STATUS.KO && !retry;

		if (isDefinitiveKo) {
			if (error?.error?.error?.error_code === 2028) {
				this.errorType = 'insufficient-data';
				setTimeout(() => {
					this.verificationFail(error);
				}, 5000);
			} else {
				this.errorType = 'robot';
				this.verificationFail(error);
			}
			return;
		}

		if (
			error?.error?.error?.error_code?.toString() === ERROR_CODE.FALLBACK_MANUAL &&
			error.error?.process?.length
		) {
			this.confirmManualUpload();
			return;
		}

		if (error?.error?.error?.error_code === 2028) {
			this.errorType = 'insufficient-data';
			setTimeout(() => {
				this.verificationFail(error);
			}, 5000);
		} else {
			this.errorType = 'robot';
			this.verificationFail(error);
		}
	}

	public changeToManualUpload(): void {
		const dialogMessage = {
			title: this.trans.instant('CONNECTIONS.IRPF.MANUAL.TITLE'),
			description: this.trans.instant('CONNECTIONS.IRPF.MANUAL.DESCRIPTION'),
			buttonPrimaryLabel: this.trans.instant('GENERAL.BUTTON.YES_ATTACH'),
			buttonSecondaryLabel: this.trans.instant('GENERAL.BUTTON.CANCEL'),
		};
		this.dialogConfirmationService.show(dialogMessage).subscribe((event: IModalClose) => {
			if (event === 'buttonPrimary') {
				this.confirmManualUpload();
			}
			this.dialogConfirmationService.hide();
		});
	}

	private confirmManualUpload(): void {
		this.loadingService.show(this.trans.instant('GENERAL.LOADING.CHANGE_TO_MANUAL'));
		this.operation.completed = true;
		const positionOperation = this.operations.findIndex((op: IOperation) => op.uuid === this.operation.uuid);
		if (positionOperation !== -1) {
			this.operations.splice(
				positionOperation,
				0,
				this.connectionsHelper.getConnectionDocType(CONNECTION_TYPE.IRPF, this.operation),
			);
			this.operationService.setOperations(this.operations);
		}
	}

	get subtitle(): string {
		if (this.tutorial) {
			return this.trans.instant('CONNECTIONS.IRPF.TUTORIAL_TITLE');
		}
		if (this.mode_otp) {
			return this.trans.instant('CONNECTIONS.IRPF.OTP_SUBTITLE_WO_PHONE');
		}
		if (!this.mode_otp) {
			return this.trans.instant('CONNECTIONS.IRPF.TITLE');
		}
		return '';
	}

	private startCountdown() {
		this.countdownSeconds = 300;
		this.updateCountdownDisplay();
		clearInterval(this.countdownInterval);
		this.countdownInterval = setInterval(() => {
			this.countdownSeconds--;
			this.updateCountdownDisplay();
			if (this.countdownSeconds <= 0) {
				clearInterval(this.countdownInterval);
				this.errorType = 'otp-expired';
			}
		}, 1000);
	}

	private updateCountdownDisplay() {
		const min = Math.floor(this.countdownSeconds / 60);
		const sec = this.countdownSeconds % 60;
		this.countdown = `${min}:${sec < 10 ? '0' : ''}${sec}`;
	}

	public isValidDni(dni: string): boolean {
		if (!dni || typeof dni !== 'string') {
			return false;
		}

		const dniRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
		if (!dniRegex.test(dni)) {
			return false;
		}

		const numberPart = dni.substring(0, 8);
		const letterPart = dni.substring(8).toUpperCase();
		const controlLetters = 'TRWAGMYFPDXBNJZSQVHLCKE';
		const calculatedLetter = controlLetters[parseInt(numberPart, 10) % 23];

		return letterPart === calculatedLetter;
	}

	public isValidNie(nie: string): boolean {
		if (!nie || typeof nie !== 'string') {
			return false;
		}

		const nieRegex = /^[XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
		if (!nieRegex.test(nie)) {
			return false;
		}

		const nieNumber = nie.replace(/^[X]/, '0').replace(/^[Y]/, '1').replace(/^[Z]/, '2');
		return this.isValidDni(nieNumber);
	}

	public nifValidator(control: AbstractControl): ValidationErrors | null {
		const value = control.value;
		if (!value) {
			return null;
		}
		const isValid = this.isValidDni(value) || this.isValidNie(value);
		return isValid ? null : { incorrectFormat: true };
	}

	private verificationFail(error: HttpErrorResponse): void {
		const retry = error?.error?.retry ?? false;
		if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
			this.operation.completed = true;
			this.operations.forEach((op: IOperation) => {
				if (op.type !== OPERATION_TYPE.FINISH) {
					op.completed = true;
				}
			});
			const foundFinish = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
			if (foundFinish) {
				(foundFinish as IFinishOperation).config.status = error?.error?.result;
			}
		}
	}

	/*
	 * Nav Help
	 */
	public navHelpChange(action: INavHelp): void {
		const actions = {
			[NavHelpAction.QR]: () => (this.isVisibleScan = true),
			[NavHelpAction.LANGUAGE]: () => this.languagePickerUi.open(),
			[NavHelpAction.HELP]: () => (this.isVisibleHelp = true),
			// [NavHelpAction.VOLUMEN]: () => this.navHelpService.toggleAudioGuide(action),
		};
		return actions[action.id]();
	}
}
